/**
 * Tooltip controller: shows a styled tooltip for any `[role*="tooltip"]` trigger
 * on hover and focus, above the trigger (flipping below when there is no room)
 * with the arrow centred on it.
 *
 * The trigger's `title` is the content; it is moved into an internal store and
 * removed so the browser's native tooltip never shows. A `title` present at show
 * time wins, so re-rendered content is picked up. A single set of delegated
 * `document` listeners drives everything, so dynamically added triggers work
 * without re-init; `init()` is an optional per-page hook that strips titles ahead
 * of the first hover.
 *
 * Usage. The trigger hosts the icon rather than being it, since `title` is
 * lifted off the trigger and an icon carrying the role would have none to show:
 *   <span role="tooltip" title="Explanatory text"><i class="icon-circle-question"></i></span>
 *
 * Imperative content (known only at runtime):
 *   Tooltip.set(element, 'Live status text');
 */

// Styles live in app/assets/tailwind/components/tooltip.css. Importmaps deliver this module
// without bundling CSS, so each subapp that renders Tooltip imports the stylesheet from its
// Tailwind entrypoint.

// Gap between the trigger and the tooltip (room for the arrow), and the minimum
// gap kept from the viewport edges.
const GAP = 8;
const EDGE_MARGIN = 4;

// Placed on the shared node and pointed at by the current trigger's
// `aria-describedby` while it is shown.
const NODE_ID = 'shared-tooltip';

// Substring match so any role that includes `tooltip` is covered.
const TRIGGER_SELECTOR = '[role*="tooltip"]';

// Guards `ready()` so the delegated `document` listeners are attached at most
// once, however many times it runs (e.g. should init ever move onto `turbo:load`,
// which fires on every navigation).
let attached = false;

export class Tooltip {
  // The single reused tooltip node, the trigger it currently describes, the map
  // of trigger -> content lifted out of each trigger's `title`, and the observer
  // that hides the tooltip when its trigger leaves the DOM.
  static node = null;
  static current = null;
  static store = new WeakMap();
  static observer = null;

  /**
   * Attach the delegated listeners that show and hide tooltips. Hover and focus
   * show; mouse-leave, blur, Escape, and scroll hide. Idempotent: only the first
   * call attaches the listeners, so it is safe to run on every navigation.
   *
   * @returns {void}
   */
  static ready() {
    if (attached) return;
    attached = true;

    // Resolve the trigger once per event and run the handler only when one is hit.
    const delegate = (type, handler) =>
      document.addEventListener(type, (event) => {
        const trigger = event.target.closest(TRIGGER_SELECTOR);
        if (trigger) handler(trigger, event);
      });

    delegate('mouseover', (trigger) => Tooltip.show(trigger));
    delegate('mouseout', (trigger, event) => {
      // Ignore moves between descendants of the same trigger.
      if (!event.relatedTarget || !trigger.contains(event.relatedTarget))
        Tooltip.hide();
    });
    delegate('focusin', (trigger) => Tooltip.show(trigger));
    delegate('focusout', () => Tooltip.hide());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') Tooltip.hide();
    });

    document.addEventListener('scroll', () => Tooltip.hide(), { capture: true, passive: true });
  }

  /**
   * Move the `title` off every trigger in `root` into the store, so the native
   * tooltip never shows. Idempotent.
   *
   * @param {Document|Element} [root=document]
   * @returns {void}
   */
  static init(root = document) {
    root.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => Tooltip.stash(trigger));
  }

  /**
   * Register `text` as a trigger's content imperatively, for elements whose
   * tooltip text is only known at runtime. Ensures the element is a tooltip
   * trigger so the delegated handlers pick it up on hover.
   *
   * @param {Element} element the trigger to annotate
   * @param {string} text the tooltip content
   * @returns {void}
   */
  static set(element, text) {
    if (!element) return;

    if (!element.getAttribute('role')?.includes('tooltip'))
      element.setAttribute('role', 'tooltip');

    element.removeAttribute('title');
    Tooltip.store.set(element, text);
    Tooltip.ensureAccessibleName(element, text);
  }

  /**
   * Show the tooltip for `trigger`, positioned and described. No-op when the
   * trigger has no content or is already the current trigger.
   *
   * @param {Element} trigger
   * @returns {void}
   */
  static show(trigger) {
    const content = Tooltip.contentFor(trigger);
    if (!content || Tooltip.current === trigger) return;

    Tooltip.hide();

    const node = Tooltip.ensureNode();
    node.querySelector('.tooltip-inner').textContent = content;
    node.style.display = 'block';
    Tooltip.position(trigger, node);
    node.classList.add('show');

    trigger.setAttribute('aria-describedby', NODE_ID);
    Tooltip.current = trigger;
    Tooltip.watch();
  }

  /**
   * Hide the shared tooltip and drop the `aria-describedby` from the trigger it
   * described.
   *
   * @returns {void}
   */
  static hide() {
    Tooltip.observer?.disconnect();

    if (Tooltip.current) {
      Tooltip.current.removeAttribute('aria-describedby');
      Tooltip.current = null;
    }

    if (Tooltip.node) {
      Tooltip.node.classList.remove('show');
      Tooltip.node.style.display = 'none';
    }
  }

  /**
   * While a tooltip is shown, hide it as soon as its trigger leaves the DOM.
   * Browsers do not reliably fire mouseout/focusout when the hovered node is
   * removed (a deleted row, a closed modal, a re-rendered component), which would
   * otherwise leave the shared box floating with nothing to point at. Active only
   * while a tooltip is shown; `hide()` disconnects it.
   *
   * @returns {void}
   */
  static watch() {
    if (!Tooltip.observer)
      Tooltip.observer = new MutationObserver(() => {
        if (Tooltip.current && !Tooltip.current.isConnected) Tooltip.hide();
      });

    Tooltip.observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Return the content for `trigger`, lazily stashing a `title` still on the
   * element (re-stashing a `title` re-added since the last show).
   *
   * @param {Element} trigger
   * @returns {string|undefined}
   */
  static contentFor(trigger) {
    Tooltip.stash(trigger);
    return Tooltip.store.get(trigger);
  }

  /**
   * Move a non-empty `title` off `trigger` into the store and preserve it as the
   * accessible name when the trigger has none of its own. No-op when there is no
   * `title` to move.
   *
   * @param {Element} trigger
   * @returns {void}
   */
  static stash(trigger) {
    const title = trigger.getAttribute('title');
    if (title === null || title === '') return;

    trigger.removeAttribute('title');
    Tooltip.store.set(trigger, title);
    Tooltip.ensureAccessibleName(trigger, title);
  }

  /**
   * Mirror `text` to `aria-label` when `trigger` would otherwise be unnamed
   * (icon-only triggers lose their accessible name when `title` is removed).
   * Leaves triggers that already have a name (visible text, `aria-label`, or
   * `aria-labelledby`) untouched.
   *
   * @param {Element} trigger
   * @param {string} text
   * @returns {void}
   */
  static ensureAccessibleName(trigger, text) {
    const named = trigger.textContent.trim() ||
      trigger.getAttribute('aria-label') ||
      trigger.getAttribute('aria-labelledby');

    if (!named) trigger.setAttribute('aria-label', text);
  }

  /**
   * Lazily create the shared tooltip node, or re-attach it to the current
   * `<body>` if it was detached.
   *
   * @returns {Element} the shared tooltip node
   */
  static ensureNode() {
    if (!Tooltip.node) {
      const node = document.createElement('div');
      node.className = 'tooltip tooltip-top';
      node.setAttribute('role', 'tooltip');
      node.id = NODE_ID;
      node.style.display = 'none';
      node.innerHTML = '<div class="tooltip-arrow"></div><div class="tooltip-inner"></div>';
      Tooltip.node = node;
    }

    if (!Tooltip.node.isConnected) document.body.appendChild(Tooltip.node);

    return Tooltip.node;
  }

  /**
   * Position `node` over `trigger`: top by default, flipping to bottom when there
   * is no room above, clamping the box into the viewport, and publishing the
   * trigger's centre so the stylesheet can re-centre the arrow on it.
   * Coordinates are document-relative (the node lives on `<body>` and is
   * `position: absolute`).
   *
   * @param {Element} trigger
   * @param {Element} node
   * @returns {void}
   */
  static position(trigger, node) {
    const rect = trigger.getBoundingClientRect();
    const tipWidth = node.offsetWidth;
    const tipHeight = node.offsetHeight;
    const { scrollX, scrollY } = window;
    const viewportWidth = document.documentElement.clientWidth;

    const flip = rect.top - tipHeight - GAP < 0;
    const top = flip
      ? rect.bottom + scrollY + GAP
      : rect.top + scrollY - tipHeight - GAP;

    const centred = rect.left + scrollX + (rect.width / 2) - (tipWidth / 2);
    const minLeft = scrollX + EDGE_MARGIN;
    const maxLeft = scrollX + viewportWidth - tipWidth - EDGE_MARGIN;
    const left = Math.max(minLeft, Math.min(centred, maxLeft));

    node.classList.toggle('tooltip-top', !flip);
    node.classList.toggle('tooltip-bottom', flip);
    node.style.top = `${Math.round(top)}px`;
    node.style.left = `${Math.round(left)}px`;

    const triggerCentreX = rect.left + scrollX + (rect.width / 2);
    node.style.setProperty('--tooltip-arrow-x', `${Math.round(triggerCentreX - left)}px`);
  }
}

export default Tooltip;
